import React, { useMemo, useState, useCallback } from 'react';
import { WORKFLOW_TABS } from '../../context/PermissionContext';

/**
 * Shared utilities and UI components for SmartWorkflowWidget views.
 * Extracted for maintainability — each role-view imports what it needs.
 */

// ── Helper: get patient display name ────────────────────────────────
export function patientName(visit) {
  return visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim() || 'Patient';
}

export const STATUS_COLORS_INLINE = {
  QUEUE:       { bg: 'rgba(59,130,246,0.12)',  text: '#3b82f6' },
  IN_PROGRESS: { bg: 'rgba(139,92,246,0.12)',  text: '#8b5cf6' },
  COMPLETED:   { bg: 'rgba(34,197,94,0.12)',   text: '#22c55e' },
  CANCELLED:   { bg: 'rgba(107,114,128,0.12)', text: '#6b7280' },
};

// Widget Shell
// S8 — `hideHeader` (default false, every existing caller unaffected) drops
// the "⚡ Smart Workflow" title row while keeping the same outer rounded
// border/background and content padding. Used ONLY by LabTechView: the S6
// status cards are that screen's own header, and the technician's dashboard
// IS the lab queue, not a sub-widget of a bigger workflow.
export function WidgetShell({ isDark, children, hideHeader = false }) {
  return (
    <div
      style={{
        borderRadius: '1rem',
        overflow: 'hidden',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <div
          style={{
            padding: '0.75rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
          }}
        >
          <span style={{ fontSize: '1rem' }}>⚡</span>
          <h3
            style={{
              margin: 0,
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: isDark ? '#f5f5f7' : '#111827',
              letterSpacing: '0.01em',
            }}
          >
            Smart Workflow
          </h3>
        </div>
      )}
      <div style={{ padding: '1rem 1.25rem' }}>{children}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════

// StatPill
export function StatPill({ label, value, color, isDark }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '1.375rem',
          height: '1.375rem',
          borderRadius: '0.375rem',
          background: `${color}22`,
          fontSize: '0.75rem',
          fontWeight: 700,
          color,
          padding: '0 0.25rem',
        }}
      >
        {value}
      </span>
      <span style={{ fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{label}</span>
    </div>
  );
}

/**
 * DoctorAvailabilityToggle — Compact pill-based status toggle for doctors.
 * Shows current availability with icon + label, click to cycle or expand dropdown.
 */
export function DoctorAvailabilityToggle({ isDark, currentAvail, options, onSelect }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      {/* Current status pill — clickable */}
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.3125rem 0.625rem', borderRadius: '2rem',
          border: `1px solid ${isDark
            ? `${currentAvail.color}33`
            : `${currentAvail.color}22`}`,
          background: isDark
            ? `${currentAvail.color}15`
            : `${currentAvail.color}0A`,
          fontSize: '0.6875rem', fontWeight: 500,
          color: currentAvail.color,
          transition: 'all 0.2s ease',
        }}
      >
        <span style={{ fontSize: '0.5rem' }}>{currentAvail.icon}</span>
        <span>{currentAvail.label}</span>
        <span style={{ fontSize: '0.5rem', opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.25rem)', left: 0,
          zIndex: 50,
          display: 'flex', flexDirection: 'column', gap: '0.125rem',
          padding: '0.25rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          background: isDark ? '#1f2937' : '#ffffff',
          boxShadow: isDark
            ? '0 4px 12px rgba(0,0,0,0.4)'
            : '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: '11rem',
        }}>
          {options.map((opt) => {
            const isSelected = opt.key === currentAvail.key;
            return (
              <button
                key={opt.key}
                onClick={() => { onSelect(opt.key); setOpen(false); }}
                style={{
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.4375rem 0.625rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: isSelected
                    ? isDark ? `${opt.color}18` : `${opt.color}0C`
                    : 'transparent',
                  fontSize: '0.75rem', fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? opt.color : isDark ? '#d4d4d8' : '#374151',
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
              >
                <span>{opt.icon}</span>
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isSelected && <span style={{ fontSize: '0.625rem' }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Default tab definitions
export const ALL_TABS = WORKFLOW_TABS;

export function orderTabsByPatterns(tabs, patterns) {
  if (!patterns?.top_sequences?.length) return tabs;

  // Build per-action frequency score from transition pairs
  const freq = {};
  for (const seq of patterns.top_sequences) {
    const actions = seq.sequence || [];
    const count = seq.frequency || 0;
    for (const a of actions) {
      freq[a] = (freq[a] || 0) + count;
    }
  }

  // Score each tab; unmatched tabs keep their default position
  const scored = tabs.map((tab, idx) => ({
    tab,
    score: freq[tab.action] || 0,
    defaultIdx: idx,
  }));

  // Sort: highest score first; ties preserve default order
  scored.sort((a, b) => b.score - a.score || a.defaultIdx - b.defaultIdx);
  return scored.map((s) => s.tab);
}

// UnifiedActionBar
export function UnifiedActionBar({
  isDark, activeTab, onTabToggle,
  onComplete, onFollowUp, onCancel, onSkip, onHistory,
  loading, patterns, detailCount,
  followUpAlreadySet, followUpDate, pendingComplete,
  excludeTabs,
}) {
  const orderedTabs = useMemo(() => {
    const ordered = orderTabsByPatterns(ALL_TABS, patterns);
    if (excludeTabs?.length) {
      const hidden = new Set(excludeTabs);
      return ordered.filter(t => !hidden.has(t.key));
    }
    return ordered;
  }, [patterns, excludeTabs]);

  const tabBtn = (tab, isActive) => {
    const isHistoryTab = tab.key === 'history';
    return (
      <button
        key={tab.key}
        onClick={() => isHistoryTab ? onHistory() : onTabToggle(tab.key)}
        style={{
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
          padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
          fontSize: '0.6875rem', fontWeight: isActive ? 600 : 400,
          border: isActive
            ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          background: isActive
            ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
            : 'transparent',
          color: isActive
            ? '#3b82f6'
            : isDark ? '#d4d4d8' : '#374151',
          transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        }}
      >
        <span>{tab.icon}</span>
        <span>{tab.label}</span>
        {isActive && !isHistoryTab && (
          <span style={{ fontSize: '0.5rem', marginLeft: '0.1rem', opacity: 0.7 }}>▲</span>
        )}
      </button>
    );
  };

  const hideStandaloneFollowUp = excludeTabs?.length > 0; // Doctor: follow-up is in the prescription form

  return (
    <div style={{
      display: 'flex', gap: '0.375rem', flexWrap: 'wrap',
      alignItems: 'center', marginBottom: '0.25rem',
    }}>
      {/* Data-entry tabs — ordered by backend usage patterns, Follow Up inserted after Prescription */}
      {orderedTabs.map((tab) => (
        <React.Fragment key={tab.key}>
          {tabBtn(tab, activeTab === tab.key)}
          {tab.key === 'prescription' && !hideStandaloneFollowUp && !followUpAlreadySet && (
            <button
              onClick={onFollowUp}
              disabled={loading}
              style={{
                cursor: loading ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                fontSize: '0.6875rem', fontWeight: 500,
                border: `1px solid ${isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.15)'}`,
                background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.05)',
                color: isDark ? '#60a5fa' : '#3b82f6', transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              🔄 Follow Up
            </button>
          )}
          {tab.key === 'prescription' && !hideStandaloneFollowUp && followUpAlreadySet && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
              fontSize: '0.6875rem', fontWeight: 500,
              border: `1px solid ${isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.15)'}`,
              background: isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.05)',
              color: isDark ? '#34d399' : '#059669',
            }}>
              ✅ Follow Up{followUpDate ? ` · ${followUpDate}` : ''}
            </span>
          )}
        </React.Fragment>
      ))}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Complete / Confirm Complete */}
      <button
        onClick={onComplete}
        disabled={loading}
        style={{
          cursor: loading ? 'wait' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
          padding: pendingComplete ? '0.4375rem 0.75rem' : '0.375rem 0.625rem',
          borderRadius: '0.5rem',
          fontSize: '0.6875rem', fontWeight: 600,
          border: pendingComplete ? '1.5px solid #22c55e' : 'none',
          background: pendingComplete
            ? (isDark ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.18)')
            : (isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'),
          color: '#22c55e',
          transition: 'background 0.15s, opacity 0.15s, padding 0.15s, border 0.15s',
          animation: pendingComplete ? 'pulse 2s infinite' : 'none',
        }}
      >
        {pendingComplete ? '✓ Confirm Complete' : '✓ Complete'}
      </button>

      {/* Skip — Not Present */}
      {onSkip && (
        <button
          onClick={onSkip}
          disabled={loading}
          style={{
            cursor: loading ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
            fontSize: '0.6875rem', fontWeight: 500, border: 'none',
            background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)',
            color: isDark ? '#f59e0b' : '#d97706', transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          ⏭ Not Present
        </button>
      )}

      {/* Cancel — only when handler provided */}
      {onCancel && (
        <button
          onClick={onCancel}
          disabled={loading}
          style={{
            cursor: loading ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
            fontSize: '0.6875rem', fontWeight: 500, border: 'none',
            background: isDark ? 'rgba(107,114,128,0.12)' : 'rgba(107,114,128,0.08)',
            color: isDark ? '#9ca3af' : '#6b7280', transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          ✕ Cancel
        </button>
      )}
    </div>
  );
}

// ConfirmDialog
export function ConfirmDialog({ isDark, title, message, subtitle, confirmLabel, confirmColor, onConfirm, onCancel }) {
  const bg = isDark ? '#1f2937' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '360px',
        background: bg,
        borderRadius: '1rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        animation: 'slideDown 0.2s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.25rem 0.75rem',
          textAlign: 'center',
        }}>
          <div style={{
            width: '2.5rem', height: '2.5rem', borderRadius: '50%',
            background: `${confirmColor || '#ef4444'}18`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '0.625rem',
          }}>
            <span style={{ fontSize: '1.125rem' }}>⚠️</span>
          </div>
          <h3 style={{
            margin: 0, fontSize: '0.9375rem', fontWeight: 700,
            color: isDark ? '#f5f5f7' : '#111827',
          }}>
            {title}
          </h3>
          <p style={{
            margin: '0.5rem 0 0', fontSize: '0.8125rem',
            color: isDark ? '#d4d4d8' : '#374151',
            lineHeight: 1.5,
          }}>
            {message}
          </p>
          {subtitle && (
            <p style={{
              margin: '0.25rem 0 0', fontSize: '0.6875rem',
              color: isDark ? '#6b7280' : '#9ca3af',
            }}>
              {subtitle}
            </p>
          )}
        </div>
        {/* Actions */}
        <div style={{
          display: 'flex', gap: '0.5rem',
          padding: '0.75rem 1.25rem 1.25rem',
        }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, cursor: 'pointer',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: `1px solid ${border}`,
              background: 'transparent',
              fontSize: '0.8125rem', fontWeight: 500,
              color: isDark ? '#d4d4d8' : '#374151',
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, cursor: 'pointer',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: 'none',
              background: confirmColor || '#ef4444',
              fontSize: '0.8125rem', fontWeight: 600,
              color: '#fff',
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// CancelConfirmDialog
export function CancelConfirmDialog({ isDark, name, onConfirm, onCancel }) {
  const [comment, setComment] = useState('');
  const bg = isDark ? '#1f2937' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const canConfirm = comment.trim().length >= 3;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '400px',
        background: bg,
        borderRadius: '1rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        animation: 'slideDown 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '1.25rem 1.25rem 0.75rem', textAlign: 'center' }}>
          <div style={{
            width: '2.5rem', height: '2.5rem', borderRadius: '50%',
            background: 'rgba(239,68,68,0.12)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '0.625rem',
          }}>
            <span style={{ fontSize: '1.125rem' }}>⚠️</span>
          </div>
          <h3 style={{
            margin: 0, fontSize: '0.9375rem', fontWeight: 700,
            color: isDark ? '#f5f5f7' : '#111827',
          }}>
            Cancel Visit
          </h3>
          <p style={{
            margin: '0.5rem 0 0', fontSize: '0.8125rem',
            color: isDark ? '#d4d4d8' : '#374151', lineHeight: 1.5,
          }}>
            Are you sure you want to cancel the visit for <strong>{name}</strong>?
          </p>
        </div>

        {/* Mandatory comment */}
        <div style={{ padding: '0 1.25rem' }}>
          <label style={{
            display: 'block', fontSize: '0.6875rem', fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            marginBottom: '0.375rem',
          }}>
            Reason for cancellation <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Please provide a reason (required, min 3 chars)…"
            rows={3}
            autoFocus
            style={{
              width: '100%', resize: 'vertical',
              padding: '0.5rem 0.625rem',
              borderRadius: '0.5rem',
              border: `1px solid ${comment.trim().length > 0 && !canConfirm
                ? 'rgba(239,68,68,0.4)'
                : border}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
              color: isDark ? '#f5f5f7' : '#111827',
              fontSize: '0.8125rem',
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          {comment.trim().length > 0 && !canConfirm && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.625rem', color: '#ef4444' }}>
              Please enter at least 3 characters
            </p>
          )}
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex', gap: '0.5rem',
          padding: '0.75rem 1.25rem 1.25rem',
        }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, cursor: 'pointer',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: `1px solid ${border}`,
              background: 'transparent',
              fontSize: '0.8125rem', fontWeight: 500,
              color: isDark ? '#d4d4d8' : '#374151',
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            Go Back
          </button>
          <button
            onClick={() => canConfirm && onConfirm(comment.trim())}
            disabled={!canConfirm}
            style={{
              flex: 1, cursor: canConfirm ? 'pointer' : 'not-allowed',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: 'none',
              background: canConfirm ? '#ef4444' : isDark ? '#374151' : '#d1d5db',
              fontSize: '0.8125rem', fontWeight: 600,
              color: '#fff',
              opacity: canConfirm ? 1 : 0.6,
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            Cancel Visit
          </button>
        </div>
      </div>
    </div>
  );
}

// FollowUpDialog
/* ──────────────────────────────────────────────────────────────
   CompletionDialog — Quick 1-2 second completion metadata
   ────────────────────────────────────────────────────────────── */
export function FollowUpDialog({ isDark, name, onConfirm, onCancel }) {
  const [reportNeeded, setReportNeeded] = useState(false);
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(null);

  const bg = isDark ? '#1f2937' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  // Quick follow-up presets — clickable chips
  const followUpPresets = useMemo(() => {
    const d = new Date();
    const fmt = (date) => date.toISOString().split('T')[0];
    const add = (days) => { const n = new Date(d); n.setDate(n.getDate() + days); return n; };
    return [
      { label: '3 Days',   date: fmt(add(3)) },
      { label: '5 Days',   date: fmt(add(5)) },
      { label: '1 Week',   date: fmt(add(7)) },
      { label: '2 Weeks',  date: fmt(add(14)) },
      { label: '1 Month',  date: fmt(add(30)) },
      { label: '3 Months', date: fmt(add(90)) },
    ];
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm({
      reportNeeded,
      followUpRequired,
      followUpDate: followUpRequired ? followUpDate : null,
    });
  }, [onConfirm, reportNeeded, followUpRequired, followUpDate]);

  const chipStyle = (active) => ({
    cursor: 'pointer',
    padding: '0.375rem 0.75rem',
    borderRadius: '2rem',
    border: active
      ? `1.5px solid ${isDark ? '#4ade80' : '#16a34a'}`
      : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    background: active
      ? (isDark ? 'rgba(74,222,128,0.15)' : 'rgba(22,163,74,0.08)')
      : 'transparent',
    color: active
      ? (isDark ? '#4ade80' : '#16a34a')
      : (isDark ? '#d4d4d8' : '#374151'),
    fontSize: '0.75rem',
    fontWeight: active ? 600 : 400,
    transition: 'background 0.12s, color 0.12s, border-color 0.12s',
  });

  const toggleStyle = (active, color = '#3b82f6') => ({
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
    padding: '0.5rem 0.875rem',
    borderRadius: '0.625rem',
    border: active
      ? `1.5px solid ${color}`
      : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    background: active
      ? `${color}18`
      : 'transparent',
    color: active ? color : (isDark ? '#d4d4d8' : '#374151'),
    fontSize: '0.8125rem',
    fontWeight: active ? 600 : 400,
    transition: 'background 0.12s, color 0.12s, border-color 0.12s',
    flex: 1,
    justifyContent: 'center',
  });

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '380px',
        maxHeight: 'calc(100vh - 2rem)',
        background: bg,
        borderRadius: '1rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideDown 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '1rem 1.25rem 0.5rem', textAlign: 'center', flexShrink: 0 }}>
          <div style={{
            width: '2.5rem', height: '2.5rem', borderRadius: '50%',
            background: 'rgba(59,130,246,0.12)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '0.5rem',
          }}>
            <span style={{ fontSize: '1.125rem' }}>🔄</span>
          </div>
          <h3 style={{
            margin: 0, fontSize: '0.9375rem', fontWeight: 700,
            color: isDark ? '#f5f5f7' : '#111827',
          }}>
            Add Follow Up
          </h3>
          <p style={{
            margin: '0.25rem 0 0', fontSize: '0.75rem',
            color: isDark ? '#9ca3af' : '#6b7280',
          }}>
            {name}
          </p>
        </div>

        {/* Scrollable content area */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

        {/* Report Needed */}
        <div style={{ padding: '0.5rem 1.25rem' }}>
          <div style={{
            fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: isDark ? '#6b7280' : '#9ca3af',
            marginBottom: '0.375rem',
          }}>
            Report / Test Needed?
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setReportNeeded(false)}
              style={toggleStyle(!reportNeeded, isDark ? '#6b7280' : '#9ca3af')}
            >
              No
            </button>
            <button
              onClick={() => setReportNeeded(true)}
              style={toggleStyle(reportNeeded, '#f59e0b')}
            >
              📋 Yes, Needed
            </button>
          </div>
        </div>

        {/* Follow Up Required */}
        <div style={{ padding: '0.5rem 1.25rem' }}>
          <div style={{
            fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: isDark ? '#6b7280' : '#9ca3af',
            marginBottom: '0.375rem',
          }}>
            Follow-up Required?
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => { setFollowUpRequired(false); setFollowUpDate(null); }}
              style={toggleStyle(!followUpRequired, isDark ? '#6b7280' : '#9ca3af')}
            >
              No
            </button>
            <button
              onClick={() => setFollowUpRequired(true)}
              style={toggleStyle(followUpRequired, '#3b82f6')}
            >
              🔄 Yes
            </button>
          </div>
        </div>

        {/* Follow-up date presets — only when followUpRequired */}
        {followUpRequired && (
          <div style={{ padding: '0.25rem 1.25rem 0.375rem' }}>
            <div style={{
              fontSize: '0.5625rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.04em', color: isDark ? '#6b7280' : '#9ca3af',
              marginBottom: '0.375rem',
            }}>
              When?
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
              {followUpPresets.map((p) => (
                <button
                  key={p.label}
                  onClick={() => setFollowUpDate(p.date)}
                  style={chipStyle(followUpDate === p.date)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        </div>{/* end scrollable */}

        {/* Actions — pinned at bottom */}
        <div style={{
          display: 'flex', gap: '0.5rem',
          padding: '0.75rem 1.25rem 1.25rem',
          flexShrink: 0,
          borderTop: `1px solid ${border}`,
        }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, cursor: 'pointer',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: `1px solid ${border}`,
              background: 'transparent',
              fontSize: '0.8125rem', fontWeight: 500,
              color: isDark ? '#d4d4d8' : '#374151',
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            Go Back
          </button>
          <button
            onClick={handleConfirm}
            style={{
              flex: 1, cursor: 'pointer',
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              border: 'none',
              background: '#3b82f6',
              fontSize: '0.8125rem', fontWeight: 600,
              color: '#fff',
              transition: 'background 0.15s, opacity 0.15s',
            }}
          >
            🔄 Save
          </button>
        </div>
      </div>
    </div>
  );
}


// EmptyState
export function EmptyState({ isDark, icon, title, subtitle, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '0.75rem 0' }}>
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', fontWeight: 500, color: isDark ? '#e5e7eb' : '#374151' }}>
        {title}
      </p>
      {subtitle && (
        <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
          {subtitle}
        </p>
      )}
      {action && (
        <button
          onClick={action.onClick}
          style={{
            marginTop: '0.625rem',
            cursor: 'pointer',
            padding: '0.375rem 0.875rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            background: 'transparent',
            fontSize: '0.75rem',
            fontWeight: 500,
            color: isDark ? '#d4d4d8' : '#374151',
            transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
